import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Login.css';

export default function ResetPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/password/forgot', { email });
      setSuccess('If that email is registered, a reset code has been sent.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset code');
    }
    setLoading(false);
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/api/password/verify-code', { email, code });
      if (res.data.resetToken) {
        setResetToken(res.data.resetToken);
        setStep(3);
        setSuccess('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    }
    setLoading(false);
  };

  const handleStep3 = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/password/reset', { resetToken, password });
      setSuccess('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
    setLoading(false);
  };

  const stepTitles = ['Enter Email', 'Verification Code', 'New Password'];

  return (
    <div className="login-wrapper">
      <div className="login-container-dark">
        <div className="login-logo">
          <img src="/logo.png" alt="Punt Club Logo" />
        </div>

        <h2 className="login-title">Reset Password</h2>

        {/* Step indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
          {stepTitles.map((t, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 6
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                background: step > i + 1 ? '#10b981' : step === i + 1 ? '#fbbf24' : '#3a3d4a',
                color: step === i + 1 ? '#000' : '#fff',
              }}>{i + 1}</div>
              <span style={{ fontSize: 12, color: step === i + 1 ? '#fbbf24' : '#666' }}>{t}</span>
              {i < 2 && <span style={{ color: '#3a3d4a', margin: '0 4px' }}>—</span>}
            </div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div style={{ background: '#065f46', color: '#10b981', padding: '10px 16px', borderRadius: 6, marginBottom: 16, textAlign: 'center' }}>{success}</div>}

        {step === 1 && (
          <form onSubmit={handleStep1} className="login-form">
            <p className="login-description">Enter your email address and we'll send you a verification code.</p>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)}
                required autoFocus placeholder="Enter your email" disabled={loading} />
            </div>
            <button type="submit" className="btn-warning" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <div className="forgot-password"><a href="/login">Back to Sign In</a></div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleStep2} className="login-form">
            <p className="login-description">Enter the 6-digit code sent to {email}</p>
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input type="text" id="code" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required autoFocus placeholder="Enter 6-digit code" disabled={loading}
                maxLength={6} style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' }} />
            </div>
            <button type="submit" className="btn-warning" disabled={loading || code.length < 6}>
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" onClick={() => { setStep(1); setError(''); setSuccess(''); }}
                style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>
                Back
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleStep3} className="login-form">
            <p className="login-description">Choose a new password for your account.</p>
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)}
                required autoFocus placeholder="Minimum 8 characters" disabled={loading} />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input type="password" id="confirmPassword" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required placeholder="Confirm your password" disabled={loading} />
            </div>
            <button type="submit" className="btn-warning" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
